/**
 * A lightweight, localStorage-only wishlist.
 *
 * IMPORTANT — honesty about scope: this does not sync across devices or browsers, and does not
 * survive the shopper clearing site data. There is no backend behind it. That limitation is
 * documented for the operator in `blocks/wishlist-button.liquid`'s schema and must not be papered
 * over by anything this file renders.
 *
 * Product data for the saved-items list is fetched live via the Section Rendering API against the
 * theme's own "predictive-search" section (the same technique `recently-viewed.js` uses and the same
 * one `predictive-search.js` already uses for its own "recently viewed" row), so saved items always
 * show their real, current title/price/image rather than a snapshot taken at save time.
 */
import { sectionRenderer } from '@theme/section-renderer';

const STORAGE_KEY = 'wishlistProductIds';
const BUTTON_SELECTOR = '[data-wishlist-button]';
const LIST_SELECTOR = '[data-wishlist-list]';

/** @returns {string[]} */
function getSavedIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** @param {string[]} ids */
function setSavedIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private browsing, quota, etc.) — fail silently, button reverts on reload.
  }
}

/** @param {string} id */
function isSaved(id) {
  return getSavedIds().includes(id);
}

/** @param {string} id */
function toggleSaved(id) {
  const ids = getSavedIds();
  const index = ids.indexOf(id);
  if (index === -1) {
    ids.unshift(id);
  } else {
    ids.splice(index, 1);
  }
  setSavedIds(ids);
  return index === -1;
}

/** @param {Element} button */
function paintButton(button) {
  const id = button.getAttribute('data-product-id');
  if (!id) return;

  const saved = isSaved(id);
  const savedLabel = button.getAttribute('data-label-saved') || 'Saved to wishlist';
  const saveLabel = button.getAttribute('data-label-save') || 'Save to wishlist';

  button.setAttribute('aria-pressed', String(saved));
  button.setAttribute('aria-label', saved ? savedLabel : saveLabel);

  const labelEl = button.querySelector('[data-wishlist-button-text]');
  if (labelEl) labelEl.textContent = saved ? savedLabel : saveLabel;
}

function initButtons() {
  const buttons = document.querySelectorAll(BUTTON_SELECTOR);

  buttons.forEach((button) => {
    paintButton(button);
    button.addEventListener('click', () => {
      const id = button.getAttribute('data-product-id');
      if (!id) return;
      toggleSaved(id);

      // Repaint every button for this product on the page (normally just this one).
      document
        .querySelectorAll(`${BUTTON_SELECTOR}[data-product-id="${id}"]`)
        .forEach((match) => paintButton(match));
    });
  });
}

/**
 * @param {string[]} ids
 * @returns {Promise<Element[]>}
 */
async function fetchProductCards(ids) {
  if (ids.length === 0 || typeof Theme === 'undefined' || !Theme.routes?.search_url) return [];

  const url = new URL(Theme.routes.search_url, window.location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');

  let html;
  try {
    html = await sectionRenderer.getSectionHTML('predictive-search', false, url);
  } catch {
    return [];
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const items = [...parsed.querySelectorAll('[ref="recentlyViewedItems[]"]')];

  return items
    .map((item) => item.firstElementChild)
    .filter((card) => card && card.textContent && card.textContent.trim().length > 0);
}

/** @param {Element} root */
async function initList(root) {
  const emptyState = root.querySelector('[data-wishlist-empty]');
  const grid = root.querySelector('[data-wishlist-grid]');
  const status = root.querySelector('[data-wishlist-status]');
  if (!grid) return;

  const ids = getSavedIds();
  if (ids.length === 0) return;

  const cards = await fetchProductCards(ids);
  if (cards.length === 0) return;

  for (const card of cards) {
    const li = document.createElement('li');
    li.className = 'wishlist-list__item';
    li.append(card);
    grid.append(li);
  }

  if (emptyState) emptyState.hidden = true;
  grid.hidden = false;
  if (status) status.textContent = `${cards.length} saved ${cards.length === 1 ? 'item' : 'items'}`;
}

document.querySelectorAll(BUTTON_SELECTOR).length && initButtons();
document.querySelectorAll(LIST_SELECTOR).forEach((root) => initList(root));
